import { getOpxHostApp, MAC_OUTLOOK, OUTLOOK_DESKTOP } from 'owa-config';
import type { IStyle } from '@fluentui/style-utilities';
import React from 'react';

import classNames from 'classnames';

/** Object containing per-client stylesheets for a given component */
interface ClientStyles {
    /** Base stylesheet (OWA) */
    base?: string | {};
    /** Desktop client stylesheet */
    desktop?: string | {};
    /** Mac client stylesheet */
    mac?: string | {};
}

/**
 * Returns a memoized callback getClass for dynamically generating client-aware styling,
 * by combining classes matching the provided `className` from client stylesheets
 * that are relevant to the current user context. Use this for opx host specific class names.
 *
 * Returns a memoized callback getStyle for dynamically generating client-aware styling,
 * by combining styles matching the provided `className` from client stylesheets
 * that are relevant to the current user context. Use this for opx host specific styles object.
 */
export function useClientStyles(styles: ClientStyles) {
    const stylesRef = React.useRef(styles);

    const getClass: (string) => string | undefined = React.useCallback((className: string) => {
        const { base = {}, desktop = {}, mac = {} } = stylesRef.current;
        switch (getOpxHostApp()) {
            case OUTLOOK_DESKTOP:
                return classNames(base[className], desktop?.[className]);
            case MAC_OUTLOOK:
                return classNames(base[className], mac?.[className]);
            default:
                return base[className];
        }
    }, []);

    const getStyle: (string) => IStyle | undefined = React.useCallback((className: string) => {
        const { base = {}, desktop = {}, mac = {} } = stylesRef.current;
        switch (getOpxHostApp()) {
            case OUTLOOK_DESKTOP:
                return { ...base[className], ...desktop[className] };
            case MAC_OUTLOOK:
                return { ...base[className], ...mac[className] };
            default:
                return base[className];
        }
    }, []);

    return { getClass, getStyle };
}
